import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  ExpandableSection,
  Form,
  FormField,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  ProgressBar,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";
import {
  ChatUatContextReference,
  ProjectValidationCase,
  ProjectValidationFieldResult,
  ProjectValidationRunSummary,
  Stage,
} from "../../lusca-search-api/generated-src";
import useUatProjectValidation, {
  MAX_ASANA_PROJECT_QUERY_CHARS,
  MIN_ASANA_PROJECT_QUERY_CHARS,
} from "../../hooks/useUatProjectValidation";
import "./chatUat.scss";

/* AWSUI table cell callbacks are render functions, not component types. */
/* eslint-disable react/no-unstable-nested-components */

interface UatScopeValidationPageProps {
  onInvestigate: (reference: ChatUatContextReference) => void;
}

type AssessmentFilter = "all" | ProjectValidationCase["assessment"];

export const ASANA_PROJECT_SEARCH_DEBOUNCE_MS = 750;

export function useDebouncedProjectSearch(projectSearchText: string): string {
  const [projectSearchQuery, setProjectSearchQuery] = useState("");
  useEffect(() => {
    const normalizedQuery = projectSearchText.trim();
    if (
      normalizedQuery.length < MIN_ASANA_PROJECT_QUERY_CHARS ||
      normalizedQuery.length > MAX_ASANA_PROJECT_QUERY_CHARS
    ) {
      setProjectSearchQuery(normalizedQuery);
      return undefined;
    }
    const timer = window.setTimeout(
      () => setProjectSearchQuery(normalizedQuery),
      ASANA_PROJECT_SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [projectSearchText]);
  return projectSearchQuery;
}

const ASSESSMENT_FILTERS: {
  label: string;
  value: AssessmentFilter;
}[] = [
  { label: "All assessments", value: "all" },
  { label: "Wrong order", value: "wrong_order" },
  { label: "Accounting mismatch", value: "accounting_mismatch" },
  { label: "Cannot verify", value: "cannot_verify" },
  { label: "Incomplete scope", value: "incomplete_scope" },
  { label: "Matches intended test", value: "matches_scope" },
];

function verdictOrder(item: ProjectValidationCase): number {
  return {
    wrong_order: 0,
    accounting_mismatch: 1,
    cannot_verify: 2,
    incomplete_scope: 3,
    matches_scope: 4,
  }[item.assessment];
}

function verdictIndicator(item: ProjectValidationCase): React.ReactNode {
  if (item.assessment === "wrong_order") {
    return <StatusIndicator type="error">Wrong order</StatusIndicator>;
  }
  if (item.assessment === "accounting_mismatch") {
    return <StatusIndicator type="error">Accounting mismatch</StatusIndicator>;
  }
  if (item.assessment === "incomplete_scope") {
    return <StatusIndicator type="warning">Incomplete scope</StatusIndicator>;
  }
  if (item.assessment === "cannot_verify") {
    return <StatusIndicator type="warning">Cannot verify</StatusIndicator>;
  }
  return <StatusIndicator type="success">Pass</StatusIndicator>;
}

function fieldIndicator(field: ProjectValidationFieldResult): React.ReactNode {
  if (field.reasonCode === "ACCOUNTING_APPROVED") {
    return <StatusIndicator type="success">Pass</StatusIndicator>;
  }
  const { verdict } = field;
  if (verdict === "mismatch") {
    return <StatusIndicator type="error">Mismatch</StatusIndicator>;
  }
  if (verdict === "not_verifiable") {
    return <StatusIndicator type="warning">Not verifiable</StatusIndicator>;
  }
  if (verdict === "not_applicable") {
    return <StatusIndicator type="stopped">Not applicable</StatusIndicator>;
  }
  return <StatusIndicator type="success">Match</StatusIndicator>;
}

function displayValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "Not available";
  }
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).join(" → ");
  }
  return String(value);
}

function nextStep(item: ProjectValidationCase): string {
  if (item.assessment === "wrong_order") {
    return "Retest with correct order";
  }
  if (item.assessment === "accounting_mismatch") {
    return "Investigate accounting";
  }
  if (item.assessment === "incomplete_scope") {
    return "Check current Asana status";
  }
  if (item.assessment === "cannot_verify") {
    return "Review missing evidence";
  }
  return "No action";
}

function runLabel(run: ProjectValidationRunSummary): string {
  const attention =
    run.counts.mismatch + run.counts.needsReview + run.counts.incomplete;
  return `${new Date(run.completedAt).toLocaleString()} · ${
    attention === 0 ? "All matched" : `${attention} to review`
  }`;
}

function fieldVerdictOrder(
  verdict: ProjectValidationFieldResult["verdict"]
): number {
  return {
    mismatch: 0,
    not_verifiable: 1,
    match: 2,
    not_applicable: 3,
  }[verdict];
}

export default function UatScopeValidationPage({
  onInvestigate,
}: UatScopeValidationPageProps) {
  const [searchParams] = useSearchParams();
  const requestedStage = searchParams.get("stage");
  const stage = Object.values(Stage).includes(requestedStage as Stage)
    ? (requestedStage as Stage)
    : Stage.Prod;
  const [editingConnection, setEditingConnection] = useState(false);
  const [projectSearchText, setProjectSearchText] = useState("");
  const projectSearchQuery = useDebouncedProjectSearch(projectSearchText);
  const workflow = useUatProjectValidation(
    stage,
    editingConnection,
    projectSearchQuery
  );
  const [disconnectVisible, setDisconnectVisible] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<SelectProps.Option | null>(null);
  const [filteringText, setFilteringText] = useState("");
  const [assessmentFilter, setAssessmentFilter] =
    useState<AssessmentFilter>("all");
  const [selectedCase, setSelectedCase] = useState<ProjectValidationCase>();

  const cases = useMemo(
    () => workflow.selectedRun?.cases ?? [],
    [workflow.selectedRun?.cases]
  );
  const sortedCases = useMemo(
    () =>
      [...cases].sort(
        (left, right) =>
          verdictOrder(left) - verdictOrder(right) ||
          left.taskName.localeCompare(right.taskName)
      ),
    [cases]
  );
  const filteredCases = useMemo(() => {
    const needle = filteringText.trim().toLowerCase();
    return sortedCases.filter((item) => {
      if (assessmentFilter !== "all" && item.assessment !== assessmentFilter) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [
        item.taskName,
        item.orderId,
        item.fields.territory?.expected,
        item.fields.product?.expected,
        item.fields.contentType?.expected,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [assessmentFilter, filteringText, sortedCases]);
  const assessmentCounts = useMemo(
    () =>
      cases.reduce(
        (counts, item) => ({
          ...counts,
          [item.assessment]: counts[item.assessment] + 1,
        }),
        {
          matches_scope: 0,
          wrong_order: 0,
          accounting_mismatch: 0,
          cannot_verify: 0,
          incomplete_scope: 0,
        }
      ),
    [cases]
  );

  useEffect(() => {
    setSelectedCase((current) => {
      const currentCase = current
        ? filteredCases.find((item) => item.taskGid === current.taskGid)
        : undefined;
      return currentCase ?? filteredCases[0];
    });
  }, [filteredCases, workflow.selectedRunId]);

  const { state } = workflow;
  if (!state && workflow.isLoading) {
    return (
      <Box textAlign="center" padding="xxl">
        <StatusIndicator type="loading">Loading validation</StatusIndicator>
      </Box>
    );
  }
  if (!state) {
    return (
      <Alert type="error" header="Validation unavailable">
        {workflow.error ?? "UAT Insights could not load validation state."}
      </Alert>
    );
  }

  const activeRun = workflow.selectedRun?.run ?? workflow.runs[0];
  const jobProgress = workflow.job?.progress;
  const isJobActive = workflow.isStarting || workflow.isPolling;
  const showConnectionEditor = editingConnection || !state.asanaLink;
  const showAuthorization = !workflow.isAsanaAuthorized;
  const attentionCount =
    assessmentCounts.wrong_order +
    assessmentCounts.accounting_mismatch +
    assessmentCounts.cannot_verify +
    assessmentCounts.incomplete_scope;
  const projectOptions = workflow.projects.map((project) => ({
    label: project.name,
    value: project.gid,
    description: project.url,
  }));
  let projectSearchEmpty = "No matching active Asana projects.";
  if (projectSearchText.trim().length < MIN_ASANA_PROJECT_QUERY_CHARS) {
    projectSearchEmpty = "Type at least 2 characters to search.";
  } else if (projectSearchText.trim().length > MAX_ASANA_PROJECT_QUERY_CHARS) {
    projectSearchEmpty = "Use at most 100 characters to search.";
  }

  const saveConnection = async () => {
    const selectedProjectGid = selectedProject?.value;
    if (!selectedProjectGid) {
      return;
    }
    await workflow.saveAsanaLink({
      projectGid: selectedProjectGid,
      version: state.version,
    });
    setEditingConnection(false);
    setSelectedProject(null);
    setProjectSearchText("");
  };

  const disconnect = async () => {
    await workflow.disconnectAsana();
    setDisconnectVisible(false);
    setEditingConnection(true);
    setSelectedProject(null);
    setProjectSearchText("");
  };

  const investigate = (item: ProjectValidationCase) => {
    if (!activeRun) {
      return;
    }
    const reference: ChatUatContextReference = {
      runId: activeRun.runId,
      taskGid: item.taskGid,
    };
    if (item.orderId) {
      reference.orderId = item.orderId;
    }
    onInvestigate(reference);
  };

  const fieldRows = selectedCase
    ? Object.entries(selectedCase.fields).sort(
        ([leftName, left], [rightName, right]) =>
          fieldVerdictOrder(left.verdict) - fieldVerdictOrder(right.verdict) ||
          leftName.localeCompare(rightName)
      )
    : [];
  const selectedAssessmentOption =
    ASSESSMENT_FILTERS.find(({ value }) => value === assessmentFilter) ??
    ASSESSMENT_FILTERS[0];

  return (
    <>
      <ContentLayout
        defaultPadding
        header={
          <Header
            variant="h1"
            description="Keep the validation queue and selected evidence visible together, then open a focused investigation in Chat."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                {state.asanaLink && (
                  <Button
                    iconName="external"
                    href={state.asanaLink.projectUrl}
                    target="_blank"
                  >
                    Open Asana
                  </Button>
                )}
                <Button
                  variant="primary"
                  iconName="play"
                  loading={isJobActive}
                  disabled={
                    !workflow.isAsanaAuthorized ||
                    !state.asanaLink ||
                    isJobActive
                  }
                  onClick={() => {
                    workflow.startValidation().catch(() => undefined);
                  }}
                >
                  Validate project
                </Button>
              </SpaceBetween>
            }
          >
            UAT Validation
          </Header>
        }
      >
        <SpaceBetween size="l">
          {(() => {
            if (showAuthorization) {
              return (
                <Container
                  header={
                    <Header
                      variant="h2"
                      description="Authorize UAT Insights to read the Asana projects available to your account."
                    >
                      {workflow.asanaAuthorizationRequired
                        ? "Reconnect Asana"
                        : "Connect Asana"}
                    </Header>
                  }
                >
                  <SpaceBetween size="m">
                    {workflow.asanaAuthorizationError && (
                      <Alert type="error">
                        {workflow.asanaAuthorizationError}
                      </Alert>
                    )}
                    <Box float="right">
                      <Button
                        variant="primary"
                        loading={workflow.isConnectingAsana}
                        onClick={() => {
                          workflow
                            .beginAsanaAuthorization(
                              workflow.asanaAuthorizationRequired
                            )
                            .catch(() => undefined);
                        }}
                      >
                        {workflow.asanaAuthorizationRequired
                          ? "Reconnect Asana"
                          : "Connect Asana"}
                      </Button>
                    </Box>
                  </SpaceBetween>
                </Container>
              );
            }
            if (showConnectionEditor) {
              return (
                <Container
                  header={
                    <Header
                      variant="h2"
                      description="Choose from the active Asana projects available to your account."
                      actions={
                        <Button
                          loading={workflow.isConnectingAsana}
                          onClick={() => {
                            workflow
                              .beginAsanaAuthorization(true)
                              .catch(() => undefined);
                          }}
                        >
                          Reconnect Asana
                        </Button>
                      }
                    >
                      {state.asanaLink
                        ? "Change Asana project"
                        : "Choose Asana project"}
                    </Header>
                  }
                >
                  <Form errorText={workflow.saveLinkError}>
                    <SpaceBetween size="m">
                      <FormField label="Asana project">
                        <Select
                          ariaLabel="Asana project"
                          selectedOption={selectedProject}
                          options={projectOptions}
                          filteringType="manual"
                          filteringAriaLabel="Filter Asana projects"
                          filteringPlaceholder="Type at least 2 characters"
                          placeholder="Choose a project"
                          statusType={
                            workflow.projectsError
                              ? "error"
                              : workflow.isLoadingProjects
                              ? "loading"
                              : "finished"
                          }
                          loadingText="Searching Asana projects"
                          finishedText={
                            workflow.hasMoreProjects
                              ? "More matches exist. Keep typing to narrow the search."
                              : undefined
                          }
                          empty={projectSearchEmpty}
                          errorText={workflow.projectsError}
                          recoveryText="Retry"
                          onLoadItems={({ detail }) => {
                            const nextSearchText = detail.filteringText;
                            if (
                              nextSearchText.trim() &&
                              nextSearchText.trim() !== projectSearchText.trim()
                            ) {
                              setSelectedProject(null);
                            }
                            setProjectSearchText(nextSearchText);
                            if (detail.samePage && workflow.projectsError) {
                              workflow
                                .refreshAsanaProjects()
                                .catch(() => undefined);
                            }
                          }}
                          onChange={({ detail }) =>
                            setSelectedProject(detail.selectedOption)
                          }
                        />
                      </FormField>
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          variant="primary"
                          loading={workflow.isSavingLink}
                          disabled={!selectedProject?.value}
                          onClick={() => {
                            saveConnection().catch(() => undefined);
                          }}
                        >
                          {state.asanaLink ? "Change project" : "Use project"}
                        </Button>
                        {state.asanaLink && (
                          <>
                            <Button
                              onClick={() => {
                                setSelectedProject(null);
                                setProjectSearchText("");
                                setEditingConnection(false);
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              iconName="remove"
                              disabled={isJobActive}
                              onClick={() => setDisconnectVisible(true)}
                            >
                              Remove link
                            </Button>
                          </>
                        )}
                      </SpaceBetween>
                    </SpaceBetween>
                  </Form>
                </Container>
              );
            }
            return (
              <section className="chat-uat__project-card">
                <div className="chat-uat__asana-mark" aria-hidden="true">
                  <span />
                  <span />
                </div>
                <div className="chat-uat__project-copy">
                  <div className="chat-uat__eyebrow">
                    Asana project
                    <span className="chat-uat__connected">Connected</span>
                  </div>
                  <Link href={state.asanaLink?.projectUrl} external>
                    {state.asanaLink?.projectName}
                  </Link>
                  {activeRun && (
                    <span className="chat-uat__project-meta">
                      {activeRun.stage.toUpperCase()} · {cases.length} cases ·{" "}
                      {attentionCount} need attention
                    </span>
                  )}
                </div>
                <div className="chat-uat__project-actions">
                  <Button
                    iconName="edit"
                    onClick={() => {
                      setSelectedProject(
                        state.asanaLink
                          ? {
                              label: state.asanaLink.projectName,
                              value: state.asanaLink.projectGid,
                              description: state.asanaLink.projectUrl,
                            }
                          : null
                      );
                      setProjectSearchText("");
                      setEditingConnection(true);
                    }}
                  >
                    Change project
                  </Button>
                </div>
              </section>
            );
          })()}

          {workflow.jobError && (
            <Alert
              type="warning"
              header="Validation did not complete"
              action={
                state.asanaLink &&
                workflow.isAsanaAuthorized &&
                !isJobActive ? (
                  <Button
                    onClick={() => {
                      if (workflow.canResumeValidation) {
                        workflow.resumeValidation();
                      } else {
                        workflow.startValidation().catch(() => undefined);
                      }
                    }}
                  >
                    {workflow.canResumeValidation
                      ? "Retry status"
                      : "Start again"}
                  </Button>
                ) : undefined
              }
            >
              {workflow.jobError}
            </Alert>
          )}

          {isJobActive && jobProgress && (
            <Container>
              <ProgressBar
                value={
                  jobProgress.total === 0
                    ? 0
                    : Math.round(
                        (jobProgress.completed / jobProgress.total) * 100
                      )
                }
                label="Validating test cases"
                description={`${jobProgress.completed} of ${jobProgress.total}`}
                additionalInfo={
                  jobProgress.phase === "loading"
                    ? "Reading incomplete tasks from the Asana project"
                    : "Comparing expected scope with observed order facts"
                }
              />
            </Container>
          )}

          {activeRun ? (
            <section
              className="chat-uat__evidence-workspace"
              aria-label="Validation evidence workspace"
            >
              <section
                className="chat-uat__case-browser"
                aria-labelledby="uat-case-browser-title"
              >
                <header className="chat-uat__case-browser-header">
                  <div>
                    <h2 id="uat-case-browser-title">Validation cases</h2>
                    <p>
                      {attentionCount} attention ·{" "}
                      {assessmentCounts.matches_scope} matched
                    </p>
                  </div>
                  {workflow.runs.length > 1 && (
                    <Select
                      ariaLabel="Validation run"
                      selectedOption={{
                        label: runLabel(activeRun),
                        value: activeRun.runId,
                      }}
                      options={workflow.runs.map((run) => ({
                        label: runLabel(run),
                        value: run.runId,
                      }))}
                      onChange={({ detail }) =>
                        workflow.selectRun(
                          detail.selectedOption.value as string
                        )
                      }
                    />
                  )}
                </header>
                <div className="chat-uat__case-filters">
                  <TextFilter
                    filteringText={filteringText}
                    filteringPlaceholder="Search title, order, territory, or product"
                    onChange={({ detail }) =>
                      setFilteringText(detail.filteringText)
                    }
                  />
                  <Select
                    ariaLabel="Filter by assessment"
                    selectedOption={selectedAssessmentOption}
                    options={ASSESSMENT_FILTERS}
                    onChange={({ detail }) =>
                      setAssessmentFilter(
                        detail.selectedOption.value as AssessmentFilter
                      )
                    }
                  />
                </div>
                <div className="chat-uat__case-browser-list">
                  {filteredCases.length > 0 ? (
                    filteredCases.map((item) => (
                      <button
                        key={item.taskGid}
                        type="button"
                        aria-pressed={item.taskGid === selectedCase?.taskGid}
                        className={`chat-uat__case-browser-item ${
                          item.taskGid === selectedCase?.taskGid
                            ? "chat-uat__case-browser-item--selected"
                            : ""
                        }`}
                        onClick={() => setSelectedCase(item)}
                      >
                        <span className="chat-uat__case-browser-copy">
                          <strong>{item.taskName}</strong>
                          <span>
                            {displayValue(item.fields.territory?.expected)} ·{" "}
                            {displayValue(item.fields.product?.expected)} /{" "}
                            {displayValue(item.fields.contentType?.expected)}
                          </span>
                          <span>{item.orderId ?? "Order not specified"}</span>
                        </span>
                        <span className="chat-uat__case-browser-outcome">
                          {verdictIndicator(item)}
                          <span>{nextStep(item)}</span>
                        </span>
                      </button>
                    ))
                  ) : (
                    <Box
                      padding="l"
                      textAlign="center"
                      color="text-body-secondary"
                    >
                      No cases match the current filters.
                    </Box>
                  )}
                </div>
              </section>

              <section
                className="chat-uat__case-detail"
                aria-labelledby="uat-case-detail-title"
              >
                {selectedCase ? (
                  <>
                    <header className="chat-uat__case-detail-header">
                      <div>
                        <div className="chat-uat__eyebrow">
                          Selected Asana test case
                        </div>
                        <h2 id="uat-case-detail-title">
                          {selectedCase.taskName}
                        </h2>
                        <p>
                          {selectedCase.orderId ?? "Order not specified"} ·{" "}
                          {displayValue(
                            selectedCase.fields.territory?.expected
                          )}{" "}
                          ·{" "}
                          {displayValue(selectedCase.fields.product?.expected)}{" "}
                          /{" "}
                          {displayValue(
                            selectedCase.fields.contentType?.expected
                          )}
                        </p>
                      </div>
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          iconName="external"
                          href={selectedCase.taskUrl}
                          target="_blank"
                        >
                          Open Asana
                        </Button>
                        <Button
                          variant="primary"
                          iconName="gen-ai"
                          onClick={() => investigate(selectedCase)}
                        >
                          Investigate in Chat
                        </Button>
                      </SpaceBetween>
                    </header>
                    <div className="chat-uat__case-detail-body">
                      <KeyValuePairs
                        columns={4}
                        items={[
                          {
                            label: "Assessment",
                            value: verdictIndicator(selectedCase),
                          },
                          {
                            label: "Next step",
                            value: nextStep(selectedCase),
                          },
                          {
                            label: "Retest",
                            value: selectedCase.retestRecommended
                              ? "Recommended"
                              : "Not required",
                          },
                          {
                            label: "Evidence references",
                            value: String(selectedCase.sourceRefs.length),
                          },
                        ]}
                      />
                      {selectedCase.scopeErrors.length > 0 && (
                        <Alert type="warning" header="Incomplete Asana task">
                          {selectedCase.scopeErrors.join("; ")}
                        </Alert>
                      )}
                      <Table
                        items={fieldRows}
                        trackBy={(item) => item[0]}
                        columnDefinitions={[
                          {
                            id: "field",
                            header: "Field",
                            cell: ([name]) => name,
                          },
                          {
                            id: "expected",
                            header: "Expected in Asana",
                            cell: ([, field]) => displayValue(field.expected),
                          },
                          {
                            id: "observed",
                            header: "Observed order / Raven",
                            cell: ([, field]) => displayValue(field.observed),
                          },
                          {
                            id: "result",
                            header: "Result",
                            cell: ([, field]) => fieldIndicator(field),
                          },
                          {
                            id: "reason",
                            header: "Reason",
                            cell: ([, field]) => field.reason,
                          },
                        ]}
                        header={
                          <Header
                            variant="h3"
                            description="Expected Asana scope compared with the observed order and Raven evidence."
                          >
                            Field comparison
                          </Header>
                        }
                        empty={
                          <Box color="text-body-secondary">
                            No field comparison is available.
                          </Box>
                        }
                      />
                      <ExpandableSection headerText="Validation and evidence details">
                        <KeyValuePairs
                          columns={2}
                          items={[
                            {
                              label: "Run",
                              value: activeRun.runId,
                            },
                            {
                              label: "Completed",
                              value: new Date(
                                activeRun.completedAt
                              ).toLocaleString(),
                            },
                            {
                              label: "Validator version",
                              value: selectedCase.validatorVersion,
                            },
                            {
                              label: "Action catalogue version",
                              value: selectedCase.actionCatalogVersion,
                            },
                            {
                              label: "Task GID",
                              value: selectedCase.taskGid,
                            },
                            {
                              label: "Sources",
                              value:
                                selectedCase.sourceRefs.join(", ") ||
                                "No sources recorded",
                            },
                          ]}
                        />
                      </ExpandableSection>
                    </div>
                  </>
                ) : (
                  <Box
                    padding="xxl"
                    textAlign="center"
                    color="text-body-secondary"
                  >
                    Select a validation case to review its evidence.
                  </Box>
                )}
              </section>
            </section>
          ) : (
            <Container>
              <Box textAlign="center" padding="l" color="text-body-secondary">
                {state.asanaLink
                  ? "Select Validate project to compare the current Asana test cases with placed orders."
                  : "Connect an Asana project to begin."}
              </Box>
            </Container>
          )}
        </SpaceBetween>
      </ContentLayout>

      <Modal
        visible={disconnectVisible}
        onDismiss={() => setDisconnectVisible(false)}
        header="Remove Asana link?"
        closeAriaLabel="Close remove-link confirmation"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setDisconnectVisible(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={workflow.isDisconnecting}
                onClick={() => {
                  disconnect().catch(() => undefined);
                }}
              >
                Remove link
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <Box>
            This removes the selected project. Your Asana authorization and
            completed validation history remain available.
          </Box>
          {workflow.disconnectError && (
            <Alert type="error">{workflow.disconnectError}</Alert>
          )}
        </SpaceBetween>
      </Modal>
    </>
  );
}
